import React, { useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle, FileText, Upload, X } from 'lucide-react';
import VisionneuseJustificatif from './ui/VisionneuseJustificatif';

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.webp';

const formatFileSize = (size) => {
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

const JustificatifInput = ({
  file,
  onFileChange,
  required = false,
  existingUrl = '',
  existingName = '',
  // De quoi RELIRE la pièce déjà jointe. `existingUrl` ne sert plus qu'à
  // savoir qu'il y en a une : le fichier passe désormais par une route
  // authentifiée, qu'un lien nu ne sait pas atteindre.
  existingSource = '',
  existingId = null,
  title = 'Justificatif',
  description = 'Ajoutez un fichier ou prenez une photo du justificatif.',
  accentColor = '#246990'
}) => {
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [voirExistant, setVoirExistant] = useState(false);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  useEffect(() => () => stopCamera(), []);

  const openCamera = async () => {
    setCameraError('');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("La cam?ra n'est pas disponible sur cet appareil.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 0);
    } catch (error) {
      console.error('Erreur camera justificatif:', error);
      setCameraError("Impossible d'ouvrir la cam?ra. V?rifiez les autorisations.");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const capturedFile = new File([blob], `justificatif-${Date.now()}.jpg`, {
        type: 'image/jpeg'
      });
      onFileChange(capturedFile);
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const hasFile = Boolean(file);
  const hasExisting = Boolean(existingUrl);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {title} {required && <span className="text-red-500">*</span>}
          </p>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
        {(hasFile || hasExisting) && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
            <CheckCircle size={13} />
            Présent
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-[#246990]/50"
        >
          <Upload size={16} />
          Importer
        </button>
        <button
          type="button"
          onClick={openCamera}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: accentColor }}
        >
          <Camera size={16} />
          Prendre une photo
        </button>
        {(hasFile || hasExisting) && (
          <button
            type="button"
            onClick={() => onFileChange(null)}
            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
          >
            <X size={16} />
            Retirer
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={(event) => {
          const selected = event.target.files?.[0] || null;
          onFileChange(selected);
          event.target.value = '';
        }}
      />

      {cameraOpen && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="h-56 w-full rounded-lg bg-black object-cover"
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={capturePhoto}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: accentColor }}
            >
              Capturer
            </button>
            <button
              type="button"
              onClick={stopCamera}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {cameraError && (
        <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {cameraError}
        </p>
      )}

      {(hasFile || hasExisting) && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
          <FileText size={16} className="text-[#246990]" />
          <div className="min-w-0 flex-1">
            {hasFile ? (
              <>
                <p className="truncate font-medium">{file.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              </>
            ) : (
              existingSource && existingId ? (
                <button
                  type="button"
                  onClick={() => setVoirExistant(true)}
                  className="font-medium text-[#246990] hover:underline"
                >
                  {existingName || 'Justificatif existant'}
                </button>
              ) : (
                <span className="font-medium text-gray-700">
                  {existingName || 'Justificatif existant'}
                </span>
              )
            )}
          </div>
        </div>
      )}

      <VisionneuseJustificatif
        ouvert={voirExistant}
        onFermer={() => setVoirExistant(false)}
        source={existingSource}
        id={existingId}
        titre={existingName || 'Justificatif'}
        sousTitre={title}
      />
    </div>
  );
};

export default JustificatifInput;
